<script setup>
// shadcn-style card: header (title, description), an optional toolbar row,
// content and an optional footer. `flush` removes the content padding so a
// table can run edge to edge inside the card.
//
// Controls (search, filters, buttons) go in the `toolbar` slot, on their own
// row under the title; wrap search and buttons in `.ui-card__toolbar-end` to
// push them right. The `actions` slot sits beside the title: use it for
// read-only extras such as a status badge, or for buttons when there are no
// tabs or filters to give them a row of their own (UiTableCard does this).
defineProps({
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  flush: { type: Boolean, default: false },
});
</script>

<template>
  <section class="ui-card">
    <header
      v-if="title || description || $slots.actions || $slots.header"
      class="ui-card__header"
    >
      <slot name="header">
        <div class="ui-card__heading">
          <h2 v-if="title" class="ui-card__title">{{ title }}</h2>
          <p v-if="description" class="ui-card__description">
            {{ description }}
          </p>
        </div>
      </slot>
      <div v-if="$slots.actions" class="ui-card__actions">
        <slot name="actions" />
      </div>
    </header>

    <div class="ui-card__content" :class="{ 'ui-card__content--flush': flush }">
      <div v-if="$slots.toolbar" class="ui-card__toolbar">
        <slot name="toolbar" />
      </div>
      <slot />
    </div>

    <footer v-if="$slots.footer" class="ui-card__footer">
      <slot name="footer" />
    </footer>
  </section>
</template>

<style scoped>
.ui-card {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  color: var(--foreground);
  min-width: 0;
}

.ui-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 0 24px;
}
.ui-card__heading {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.ui-card__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
}
.ui-card__description {
  margin: 0;
  font-size: 14px;
  color: var(--muted-foreground);
}
.ui-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

.ui-card__content {
  padding: 0 24px;
  min-width: 0;
}
.ui-card__content--flush {
  padding: 0;
}
.ui-card__toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 16px;
}
.ui-card__content--flush > .ui-card__toolbar {
  padding: 0 24px;
}
:slotted(.ui-card__toolbar-end) {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

.ui-card__footer {
  display: flex;
  align-items: center;
  padding: 0 24px;
}

@media (max-width: 600px) {
  .ui-card {
    gap: 16px;
    padding: 16px 0;
  }
  .ui-card__header,
  .ui-card__content:not(.ui-card__content--flush),
  .ui-card__footer,
  .ui-card__content--flush > .ui-card__toolbar {
    padding: 0 16px;
  }
  .ui-card__toolbar {
    margin-bottom: 12px;
  }
}
</style>
